import type { DomainInFile } from '@/stores/mitre.types'
import { MatrixDomain } from '@/stores/mitre.types'
import { flattenArrayOfArrays } from '@/util/arrays'
import { queryClient } from '@/util/queryClient'
import { useQuery } from '@tanstack/vue-query'
import { defineStore } from 'pinia'
import { computed, ref, watch, type ComputedRef, type Ref } from 'vue'

/**
 * The useMitreAttackStore is a custom store that provides access to MITRE ATT&CK data.
 * It defines various reactive variables and functions to fetch and manipulate the data.
 *
 * @returns {Object} The useMitreAttackStore object
 * @property {Ref<boolean>} fetched - Indicates whether the MITRE ATT&CK data has been fetched
 * @property {Ref<DomainInFile[] | undefined>} contents - The full contents of the MITRE ATT&CK domain file
 * @property {Ref<boolean>} loading - Indicates whether the data is currently being fetched
 * @property {Ref<undefined | string>} hoveredTechniqueId - The ID of the currently hovered technique
 * @property {ComputedRef<string[] | undefined>} domains - An array of all domains available in the MITRE ATT&CK data
 * @property {Function} getMatrixForDomain - Retrieves the MatrixDomain object for a given domain
 */
export const useMitreAttackStore = defineStore('mitre', () => {
  const hoveredTechniqueId: Ref<undefined | string> = ref(undefined)

  // Fetches once and caches indefinitely — the MITRE domain file only changes
  // on a build. staleTime: Infinity means every consumer (matrix view,
  // technique modal, technique cards) shares one request no matter how many
  // mount at once.
  const { data, isFetching: loading, error } = useQuery(
    {
      queryKey: ['mitreAttackObjects'],
      queryFn: async () => {
        const r = await fetch('/mitre/objects.json')
        return ((await r.json()) as DomainInFile[]) || []
      },
      staleTime: Infinity,
    },
    queryClient,
  )
  watch(error, (e) => {
    if (e) console.error(e)
  })
  const contents: ComputedRef<DomainInFile[]> = computed(() => data.value ?? [])
  const fetched: ComputedRef<boolean> = computed(() => data.value !== undefined)

  const domains: ComputedRef<string[]> = computed(
    () => contents.value.map((m) => m.domain).concat('All'),
  )
  const getMatrixForDomain = (domain: string) => {
    if (domain === 'All') {
      return new MatrixDomain({
        domain: 'All',
        objects: contents.value
          .map((m) => m.objects)
          .reduce(flattenArrayOfArrays, []),
        platforms: contents.value
          .map((m) => m.platforms)
          .reduce(flattenArrayOfArrays, [])
          .sort(),
      })
    } else {
      const match = contents.value?.find((o) => o.domain === domain)
      return match ? new MatrixDomain(match) : undefined
    }
  }

  return {
    contents,
    domains,
    fetched,
    getMatrixForDomain,
    hoveredTechniqueId,
    loading,
    techniqueFilter: MatrixDomain.techniqueFilter,
  }
})
